import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Eye, MessageCircle, Heart, Car, TrendingUp, Lock } from 'lucide-react';
import API from '../services/api';

const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    API.get('/annonces/analytics/vendeur')
      .then(res => {
        setStats(res.data.stats);
        setAnnonces(res.data.annonces);
      })
      .catch(err => setErreur(err.response?.data?.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="text-center py-20 text-gray-400">Chargement...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-white text-2xl font-black flex items-center gap-2">
            <TrendingUp size={24} style={{ color: '#F5A623' }} />
            Analytics
          </h1>
          <p style={{ color: '#F5A623' }} className="text-sm mt-1">
            Performance de vos annonces
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {erreur ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-100">
            <div style={{ background: '#F5A62315' }} className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={40} style={{ color: '#F5A623' }} />
            </div>
            <p className="text-gray-700 text-lg font-bold mb-2">{erreur}</p>
            <p className="text-gray-400 text-sm mb-6">
              Les analytics sont disponibles à partir du Pack Business
            </p>
            <Link
              to="/abonnements"
              style={{ background: '#F5A623' }}
              className="text-black px-6 py-3 rounded-xl font-bold hover:opacity-90 transition"
            >
              Voir les abonnements
            </Link>
          </div>
        ) : (
          <>
            {/* Stats globales */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Annonces', value: stats.total_annonces, icon: Car, color: '#F5A623' },
                  { label: 'Vues totales', value: stats.total_vues, icon: Eye, color: '#3b82f6' },
                  { label: 'Contacts', value: stats.total_contacts, icon: MessageCircle, color: '#8b5cf6' },
                  { label: 'Favoris', value: stats.total_favoris, icon: Heart, color: '#ef4444' }
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="bg-white rounded-2xl shadow-sm p-5 text-center border border-gray-100">
                    <div style={{ background: color + '15' }} className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Icon size={22} style={{ color }} />
                    </div>
                    <p className="text-3xl font-black text-gray-900">{value}</p>
                    <p className="text-gray-400 text-sm mt-1">{label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Tableau */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
              <div style={{ background: '#0A0A0A' }} className="p-4 flex items-center gap-2">
                <TrendingUp size={18} style={{ color: '#F5A623' }} />
                <h2 className="text-white font-bold">Performance par annonce</h2>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Annonce</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-500">
                      <Eye size={14} className="inline mr-1" />Vues
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-500">
                      <MessageCircle size={14} className="inline mr-1" />Contacts
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-500">
                      <Heart size={14} className="inline mr-1" />Favoris
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-500">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {annonces.map(annonce => (
                    <tr key={annonce.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <Link to={`/annonces/${annonce.id}`} className="font-semibold text-gray-900 hover:text-yellow-500 transition text-sm">
                          {annonce.titre}
                        </Link>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {parseInt(annonce.prix).toLocaleString('fr-FR')} FCFA
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center font-black text-gray-900">{annonce.vues}</td>
                      <td className="px-4 py-3 text-center font-black" style={{ color: '#8b5cf6' }}>{annonce.contacts}</td>
                      <td className="px-4 py-3 text-center font-black text-red-500">{annonce.favoris}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          annonce.statut === 'active' ? 'bg-green-100 text-green-600' :
                          annonce.statut === 'vendue' ? 'bg-gray-100 text-gray-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {annonce.statut}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {annonces.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Car size={40} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Aucune annonce</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;